// ===== Process Configuration Types =====
//
// MainProcessConfig is still consumed by RecoverySection.tsx (via ProcessSubsections.tsx,
// which builds a read-only config from real API fields — the backend has no recovery-config
// endpoint, so Recovery is display-only there). Everything it depends on structurally stays
// below; unrelated scaffolding from this file's original Lovable-mockup prototype (recipe
// editing, outgoing-recipe allocation, CIP records, notes/documents, a hardcoded facility
// list, and their factory functions) has been removed — none of it had a real consumer.

export type ProcessDataType = 'Specific' | 'Generic' | 'Average';
export type ProcessingType = 'Pasteurization' | 'UHT' | 'Fermentation' | 'Drying' | 'Mixing' | 'Packaging' | 'Other' | '';
export type HeatSourceType = 'Steam' | 'Hot Water' | 'Electric' | 'Natural Gas' | 'Biogas' | 'Solar Thermal' | 'Recovered Heat' | 'Other';
export type HeatSourceUnit = 'MJ/kg' | 'MJ/ton';
export type WaterSourceType = 'Tap Water' | 'Deep Wells' | 'Recovered Water';
export type WaterSourceUnit = 'L/kg' | 'L/ton' | 'm³/kg' | 'm³/ton';
export type MassUnit = 'kg' | 'ton';
export type ElectricityPerInputUnit = 'kWh/kg' | 'kWh/ton';
export type WaterPerInputUnit = 'm³/kg' | 'm³/ton';
export type HeatPerInputUnit = 'MJ/kg' | 'MJ/ton';

export interface WaterSourceRecord {
  id: string;
  source: WaterSourceType | '';
  recoveredFrom: string;
  amount: number | '';
  unit: WaterSourceUnit;
  isSaved: boolean;
  isEditing: boolean;
}

export interface HeatSource {
  id: string;
  type: HeatSourceType | '';
  amount: number | '';
  unit: HeatSourceUnit;
  recoveredSource: string;
  isSaved: boolean;
  isEditing: boolean;
}

export type RecoveredWaterUnit = 'L' | 'm³';
export type RecoveredHeatUnit = 'MJ/kg' | 'MJ/ton';

export interface MainProcessConfig {
  processingName: string;
  dataType: ProcessDataType | '';
  processingType: ProcessingType;
  facilityName: string;
  machineModelNumber: string;
  capacity: number | '';
  capacityUnit: MassUnit;
  input: number | '';
  inputUnit: MassUnit;
  output: number | '';
  outputUnit: MassUnit;
  wasteSideStream: number | '';
  wasteSideStreamUnit: MassUnit;
  electricity: number | '';
  electricityUnit: ElectricityPerInputUnit;
  water: number | '';
  waterUnit: WaterPerInputUnit;
  heat: number | '';
  heatUnit: HeatPerInputUnit;
  heatSources: HeatSource[];
  waterSources: WaterSourceRecord[];
  isRecoveringWater: boolean;
  recoveredWaterAmount: number | '';
  recoveredWaterUnit: RecoveredWaterUnit;
  isRecoveringHeat: boolean;
  recoveredHeatAmount: number | '';
  recoveredHeatUnit: RecoveredHeatUnit;
  isSaved: boolean;
}

// Waste Section
export type WasteType = 'Solid' | 'Liquid' | 'Gas' | 'Mixed';

export interface WasteRecord {
  id: string;
  streamName: string;
  type: WasteType | '';
  // Holds the API's numeric managementTypeId (as a string), not a fixed label union.
  // See WASTE_MANAGEMENT_TYPE_IDS in wasteManagement.constants.ts for the reserved id checked in the UI.
  managementType: string;
  distribution: number | '';
  distributionUnit: MassUnit;
  ingredientName: string;
  moisture: number | '';
}

// Chemical Section
// Holds the API's numeric chemicalType id (as a string), not a fixed label union.
export type ChemicalFunction = string;
// Matches PER_WEIGHT_UNITS_CHEMICAL (config/optionsConstants.ts) — the real amount-unit
// vocabulary used by the legacy chemical processing form (ChemicalRecord.tsx).
export type ChemicalUnit = 'kg' | 'ton';
// Holds the API's numeric transportType id (as a string), not a fixed label union.
export type TransportType = string;

export interface TransportRoute {
  id: string;
  type: TransportType | '';
  distance: number | '';
}

export interface ChemicalRecord {
  id: string;
  name: string;
  function: ChemicalFunction | '';
  amount: number | '';
  unit: ChemicalUnit;
  hasTransportRoute: boolean;
  routes: TransportRoute[];
}

export function createChemicalRecord(): ChemicalRecord {
  return { id: crypto.randomUUID(), name: '', function: '', amount: '', unit: 'kg', hasTransportRoute: false, routes: [] };
}
